import type { Appointment, CreateAppointmentRequest } from '@/types/appointment';

export type UserStatus = 'pending' | 'approved' | 'rejected';

export interface User {
  id: string;
  name?: string;
  email?: string;
  image?: string;
  status?: UserStatus;
  note?: string;
  createdAt?: string;
}

const API_BASE_URL = '/api';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    // biome-ignore lint/suspicious/noExplicitAny: error details are server-shaped
    public details?: any,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include',
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(errorData.error || `HTTP ${response.status}`, response.status, errorData.details);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

export const appointmentsApi = {
  // Admins receive every slot (with hero names); approved heroes receive
  // the privacy-filtered list (free slots + their own, flagged with `mine`).
  async getAppointments(): Promise<{ appointments: Appointment[] }> {
    return request('/appointments');
  },

  async createAppointment(data: CreateAppointmentRequest): Promise<{ appointment: Appointment }> {
    return request('/appointments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAppointment(id: string, data: Partial<CreateAppointmentRequest>): Promise<{ appointment: Appointment }> {
    return request(`/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteAppointment(id: string): Promise<void> {
    return request(`/appointments/${id}`, {
      method: 'DELETE',
    });
  },

  async claimAppointment(id: string): Promise<{ appointment: Appointment }> {
    return request(`/appointments/${id}/claim`, {
      method: 'POST',
    });
  },

  async unclaimAppointment(id: string): Promise<{ appointment: Appointment }> {
    return request(`/appointments/${id}/unclaim`, {
      method: 'POST',
    });
  },

  async adminUnassignAppointment(id: string): Promise<{ appointment: Appointment }> {
    return request(`/appointments/${id}/unassign`, {
      method: 'POST',
    });
  },
};

// Approval-queue operations — admin only.
export const usersApi = {
  async getPendingUsers(): Promise<{ users: User[] }> {
    return request('/users/pending');
  },

  async getUsers(): Promise<{ users: Record<string, User> }> {
    return request('/users');
  },

  async approveUser(id: string): Promise<{ status: UserStatus }> {
    return request(`/users/${id}/approve`, { method: 'POST' });
  },

  async rejectUser(id: string): Promise<{ status: UserStatus }> {
    return request(`/users/${id}/reject`, { method: 'POST' });
  },
};

// The current hero submitting their "how do you know Chris?" note.
export const meApi = {
  async submitNote(note: string): Promise<{ user: User }> {
    return request('/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ note }),
    });
  },
};

// Admin-only fixed iCal subscription URL.
export const calendarApi = {
  async getFeedUrl(): Promise<{ calendarUrl: string }> {
    return request('/calendar/feed-url');
  },
};

export { ApiError };
